import React, { FC, ReactElement } from 'react'
import { NotificationsLanguageModal } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import Modal from '../../Common/Components/Modal'
import { DocTopic } from '../../Common/Constants/Documentation'
import { LanguageContextProvider, MessageContextProvider, PromptContextProvider } from '../../Common/Contexts'
import { CommonFormContextProvider } from '../../Common/Contexts/CommonFormContext'
import { StateSetter } from '../../Common/Types/Types'
import { clearModalHelpTopic, setModalHelpTopicID } from '../../Common/Utils/HelpPortal'
import NotificationsTabs from './NotificationsTabs'
import { ApplicationResponseNotification } from '../../../NonReact/common/Types/Types'
import { $window } from '../../../NonReact/common/AkixiWindow'
// Utilities imports.
// Commons imports.

// Sub imports.
// Contexts imports.

type Props = {
    NotificationSettings: ApplicationResponseNotification
    refreshNotifications: (updatedNotifications: ApplicationResponseNotification) => void
    isOpen: boolean
    setIsOpen: StateSetter<boolean>
}

/**
 * Notifications Modal.
 *
 * @param notificationSettings - The notification settings.
 */
const NotificationsModal: FC<Props> = ({
    NotificationSettings,
    refreshNotifications,
    isOpen,
    setIsOpen
}: Props): ReactElement => {
    // Access language resources directly without context as this is top level component.
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(NotificationsLanguageModal.NAME)

    const closeModal = (): void => {
        $window.disableHotKeys = false
        // Component is controlled externally.
        setIsOpen(false)
    }

    return (
        <div>
            <CommonFormContextProvider>
                <Modal
                    isOpen={isOpen}
                    closeOnBackdropClick={true}
                    isClosableWithXButton={true}
                    inlineExitPromptSettings={{
                        message: language.get(NotificationsLanguageModal.InlineExitMessage),
                        confirmButtonText: language.get(NotificationsLanguageModal.InlineExitConfirm),
                        cancelButtonText: language.get(NotificationsLanguageModal.InlineExitCancel)
                    }}
                    onOpen={(): void => {
                        // Add contextual doc topic ID to the window object which is used
                        // in old JSP logic that handles F1 key press.
                        setModalHelpTopicID(DocTopic.NOTIFICATIONS)
                    }}
                    onClose={closeModal}
                    onExited={(): void => {
                        // Remove the contextual doc topic ID from the window that is used
                        // in old JSP logic that handles F1 key press.
                        // When removed the previous logic will work and the modal window doc topic will be not considered.
                        clearModalHelpTopic()
                    }}
                >
                    {isOpen ? (
                        <LanguageContextProvider>
                            <PromptContextProvider>
                                <MessageContextProvider>
                                    <div className='modal-title-report-config'>
                                        {language.get(NotificationsLanguageModal.NotificationsModalTitle)}
                                    </div>
                                    <div className='notifications-modal'>
                                        <NotificationsTabs
                                            NotificationSettings={NotificationSettings}
                                            closeModal={closeModal}
                                            refreshNotifications={refreshNotifications}
                                        />
                                    </div>
                                </MessageContextProvider>
                            </PromptContextProvider>
                        </LanguageContextProvider>
                    ) : (
                        <> </>
                    )}
                </Modal>
            </CommonFormContextProvider>
        </div>
    )
}

export default NotificationsModal
